import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { Loyalty } from '../utils/api';
import { LineChart, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { LOYALTY_COLORS, fmtMonth } from '../utils/theme';

// Solus → Dual → Three+ (single-app to multi-app engagement).
const LOYALTY_ORDER = ['Solus', 'Dual', 'Three+'];

function buildSeries(rows, months6, valueKey) {
  return LOYALTY_ORDER.filter(lvl => rows.some(r => r.app_loyalty === lvl)).map(lvl => {
    const byMonth = Object.fromEntries(rows.filter(r => r.app_loyalty === lvl).map(r => [r.month_id, r[valueKey]]));
    return { name: lvl, color: LOYALTY_COLORS[lvl], data: months6.map(m => byMonth[m] ?? null) };
  });
}

function last6(allMonths, selectedMonth) {
  const idx = allMonths.indexOf(selectedMonth);
  if (idx < 0) return allMonths.slice(-6);
  return allMonths.slice(Math.max(0, idx - 5), idx + 1);
}

export default function TrendsEngagementView() {
  const { category }  = useCategory();
  const { months, sessionTypes, latestMonth, loading: metaLoading } = useMetaData();

  const { month }                     = useMonth();
  const [sessionType, setSessionType] = useState('open_app');

  const effectiveCat  = category || 'ALL';
  const selectedMonth = month || latestMonth;
  const months6       = useMemo(() => last6(months, selectedMonth), [months, selectedMonth]);

  const { data, loading, error } = useApi(
    () => Loyalty.trend({ category: effectiveCat, session_type: sessionType }),
    { effectiveCat, sessionType },
    !!months.length,
  );

  const userSeries    = useMemo(() => data ? buildSeries(data, months6, 'active_users')         : [], [data, months6]);
  const sessionSeries = useMemo(() => data ? buildSeries(data, months6, 'total_15min_sessions') : [], [data, months6]);

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="trends-section-label">Engagement (App Loyalty) — 6-Month Trend</div>

      <ChartCard title="Monthly Active Users — by Loyalty (Solus / Dual / Three+)" sub={`${effectiveCat} · ${sessionType}`} style={{ marginBottom: 16 }}>
        {loading  && <ChartLoader height={300} />}
        {error    && <ChartError message={error} height={300} />}
        {!loading && userSeries.length > 0 && <LineChart categories={months6.map(fmtMonth)} series={userSeries} height={300} />}
        {!loading && !error && userSeries.length === 0 && <div className="empty-state">No data</div>}
      </ChartCard>

      <ChartCard title="Monthly Sessions — by Loyalty (Solus / Dual / Three+)" sub={`${effectiveCat} · ${sessionType}`}>
        {loading  && <ChartLoader height={300} />}
        {error    && <ChartError message={error} height={300} />}
        {!loading && sessionSeries.length > 0 && <LineChart categories={months6.map(fmtMonth)} series={sessionSeries} height={300} />}
        {!loading && !error && sessionSeries.length === 0 && <div className="empty-state">No data</div>}
      </ChartCard>
    </div>
  );
}
